import { FEATURES } from "src/app/common/enums/features";
import { SLOT } from "src/app/common/enums/slot";
import { INotification, INotificationPatch } from "src/app/common/interfaces/dialogs/notification-settings";
import { CrmModule } from "src/app/core/enums/permissions";
import { Division } from "src/app/core/interfaces/division";

export const ModulesMap = {
  [SLOT.OUTAGE_MONITORING]: CrmModule.OutageMonitoring,
  [SLOT.ACCOUNTING]: CrmModule.Accounting,
  [SLOT.THREE_YEAR_PLAN]: CrmModule.ThreeYearPlan,
  "street-lighting": CrmModule.StreetLighting,
  kwp: CrmModule.KWP,
  "grid-connection": CrmModule.GridConnection,
  news: FEATURES.NEWS
};

export type EventModuleName = keyof typeof ModulesMap;
export type EventsConfig = Record<string, string>;

type ModuleEventsConfigMap = Record<EventModuleName, Record<Division, EventsConfig> | EventsConfig>;

export class EventsMap {
  private static eventsMap: ModuleEventsConfigMap = {
    "incident-management": {
      electricity: {
        startEnd: "electricityOutageStartEnd",
        intermediate: "electricityOutageIntermediate",
        update: "electricityOutageUpdate",
        excavation: "electricityExcavation"
      },
      gas: {
        startEnd: "gasOutageStartEnd",
        intermediate: "gasOutageIntermediate",
        update: "gasOutageUpdate"
      }
    },
    accounting: {
      startEnd: "accountingInstallationUpdateNetworkProvider",
      update: "accountingInstallationUpdateMunicipality",
      intermediate: "accountingDocumentUpdate"
    },
    "three-year-plan": {
      startEnd: "municipalMeasureUpdate",
      intermediate: "networkproviderMeasureUpdate",
      dataStorage: "dataStorage"
    },
    "street-lighting": {
      startEnd: "newIncidentInstantNotification",
      intermediate: "newIncidentDailyNotification",
      customerSatisfactionSurvey: "customerSatisfactionSurvey"
    },
    kwp: {
      startEnd: "kwpStatusUpdate"
    },
    "grid-connection": {
      energyMonitorDocuments: "energyMonitorDocuments",
      kwpDocuments: "kwpDocuments",
      networkPlanningDocuments: "networkPlanningDocuments",
      energyUsageDocuments: "energyUsageDocuments",
      energyMonitorNewData: "energyMonitorNewData"
    },
    news: {
      operationNews: "operationNews",
      serviceNews: "serviceNews",
      criticalNews: "criticalNews"
    }
  };

  public static getEvent(moduleName: EventModuleName, division: Division = "electricity"): EventsConfig {
    if (moduleName === "incident-management") {
      return EventsMap.eventsMap[moduleName][division] as EventsConfig;
    }
    return EventsMap.eventsMap[moduleName] as EventsConfig;
  }

  public static mapEvents(item: INotification, moduleName: EventModuleName) {
    if (moduleName === "incident-management") {
      const gasEvents = this.mapEvent(item, this.getEvent(moduleName, "gas"));
      const electricityEvents = this.mapEvent(item, this.getEvent(moduleName, "electricity"));
      return {
        gas: gasEvents,
        gasVerifyPhone: item.gasVerifyPhone,
        electricityVerifyPhone: item.electricityVerifyPhone,
        electricity: electricityEvents,
        gasPhone: item.gasPhone,
        electricityPhone: item.electricityPhone
      };
    } else {
      return { phone: item.phone, verifyPhone: item.verifyPhone, ...this.mapEvent(item, this.getEvent(moduleName)) };
    }
  }

  public static mapData(data: INotificationPatch, moduleName: EventModuleName, divisions: Division[]) {
    if (moduleName === "incident-management") {
      return divisions.flatMap((key) => this.reverseMapEvent({ ...data, ...data[key] }, moduleName, key));
    } else {
      return this.reverseMapEvent(data, moduleName);
    }
  }

  private static mapEvent(item: any, events: EventsConfig) {
    return {
      notifyByEmail: {
        start: Boolean(item.emailEvents?.includes(events.startEnd)),
        end: Boolean(item.emailEvents?.includes(events.startEnd)),
        intermediate: events.intermediate ? Boolean(item.emailEvents?.includes(events.intermediate)) : undefined,
        update: events.update ? Boolean(item.emailEvents?.includes(events.update)) : undefined,
        excavation: events.excavation ? Boolean(item.emailEvents?.includes(events.excavation)) : undefined,
        operationNews: Boolean(item.emailEvents?.includes("operationNews")),
        serviceNews: Boolean(item.emailEvents?.includes("serviceNews")),
        criticalNews: Boolean(item.emailEvents?.includes("criticalNews")),
        dataStorage: Boolean(item.emailEvents?.includes("dataStorage")),
        energyMonitorDocuments: events.energyMonitorDocuments
          ? Boolean(item.emailEvents?.includes(events.energyMonitorDocuments))
          : undefined,
        kwpDocuments: events.kwpDocuments ? Boolean(item.emailEvents?.includes(events.kwpDocuments)) : undefined,
        networkPlanningDocuments: events.networkPlanningDocuments
          ? Boolean(item.emailEvents?.includes(events.networkPlanningDocuments))
          : undefined,
        energyUsageDocuments: events.energyUsageDocuments
          ? Boolean(item.emailEvents?.includes(events.energyUsageDocuments))
          : undefined,
        energyMonitorNewData: events.energyMonitorNewData
          ? Boolean(item.emailEvents?.includes(events.energyMonitorNewData))
          : undefined,
        energyDataDocuments:
          events.energyMonitorDocuments &&
          events.kwpDocuments &&
          events.networkPlanningDocuments &&
          events.energyUsageDocuments
            ? Boolean(item.emailEvents?.includes(events.energyMonitorDocuments)) &&
              Boolean(item.emailEvents?.includes(events.kwpDocuments)) &&
              Boolean(item.emailEvents?.includes(events.networkPlanningDocuments)) &&
              Boolean(item.emailEvents?.includes(events.energyUsageDocuments))
            : undefined,
        hasAnyEnergyDataDocument:
          events.energyMonitorDocuments &&
          events.kwpDocuments &&
          events.networkPlanningDocuments &&
          events.energyUsageDocuments
            ? Boolean(item.emailEvents?.includes(events.energyMonitorDocuments)) ||
              Boolean(item.emailEvents?.includes(events.kwpDocuments)) ||
              Boolean(item.emailEvents?.includes(events.networkPlanningDocuments)) ||
              Boolean(item.emailEvents?.includes(events.energyUsageDocuments))
            : undefined,
        customerSatisfactionSurvey: events.customerSatisfactionSurvey
          ? Boolean(item.emailEvents?.includes(events.customerSatisfactionSurvey))
          : undefined
      },
      notifyBySMS: {
        start: Boolean(item.phoneEvents?.includes(events.startEnd)),
        end: Boolean(item.phoneEvents?.includes(events.startEnd)),
        intermediate: events.intermediate ? Boolean(item.phoneEvents?.includes(events.intermediate)) : undefined,
        update: events.update ? Boolean(item.phoneEvents?.includes(events.update)) : undefined
      }
    };
  }

  private static reverseMapEvent(data: INotificationPatch, moduleName: EventModuleName, division?: Division) {
    const topic = ModulesMap[moduleName];

    return data.regionIds.flatMap((item) =>
      Object.entries(EventsMap.getEvent(moduleName, division)).flatMap(([key, event]) => {
        const result = [];
        if (data.notifyByEmail) {
          result.push({
            id: item,
            type: "region",
            channel: "email",
            topic,
            phone: null,
            event,
            subscribe: Boolean(data.notifyByEmail[key])
          });
        }

        if (data.notifyBySMS) {
          result.push({
            id: item,
            type: "region",
            channel: "phone",
            phone: data.phone || data[division].phone,
            topic,
            event,
            subscribe: Boolean(data.notifyBySMS[key])
          });
        }
        return result;
      })
    );
  }
}
